
import React, { useRef } from "react";

export interface UploadedFile {
  name: string;
  size: number;
  type: string;
  rawFile: File;
}

interface UploadDropzoneProps {
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  accept?: string; // e.g. ".pdf,.docx,image/*"
  maxFiles?: number;
  readOnly?: boolean;
}

export const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  files,
  onFilesChange,
  accept,
  maxFiles = 10,
  readOnly = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFiles(e.target.files);
    }
    // Reset input to allow re-selecting same file
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (readOnly) return;
    if (e.dataTransfer.files) {
      processFiles(e.dataTransfer.files);
    }
  };

  const processFiles = (fileList: FileList) => {
    if (files.length + fileList.length > maxFiles) {
      alert(`最多只能上傳 ${maxFiles} 個檔案`);
      return;
    }

    const newFiles: UploadedFile[] = Array.from(fileList).map((f) => ({
      name: f.name,
      size: f.size,
      type: f.type,
      rawFile: f,
    }));

    onFilesChange([...files, ...newFiles]);
  };

  const removeFile = (index: number) => {
    const updated = [...files];
    updated.splice(index, 1);
    onFilesChange(updated);
  };

  return (
    <div style={{ marginTop: 12 }}>
      {!readOnly && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: "2px dashed var(--border)",
            borderRadius: 8,
            padding: 24,
            textAlign: "center",
            cursor: "pointer",
            background: "rgba(255,255,255,0.02)",
            color: "var(--muted)",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = "#666"}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}
        >
          <div style={{ fontSize: 20, marginBottom: 8 }}>☁️</div>
          <div style={{ fontSize: 13 }}>點擊選擇或拖曳檔案至此</div>
          {accept && <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>支援格式: {accept}</div>}
          <input
            type="file"
            multiple
            ref={inputRef}
            style={{ display: "none" }}
            accept={accept}
            onChange={handleFileSelect}
          />
        </div>
      )}

      {files.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {files.map((file, idx) => (
            <div
              key={`${file.name}-${idx}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#222",
                padding: "8px 12px",
                borderRadius: 6,
                border: "1px solid #333",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                <span style={{ fontSize: 14 }}>📄</span>
                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontSize: 13, color: "#e5e7eb", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--danger)",
                    cursor: "pointer",
                    fontSize: 16,
                    padding: 4,
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
